import { ComponentProps, FC, PropsWithChildren, type JSX } from 'react';

import { StyledBox } from '@eneco/custom-components/flows/Overview';
import useDC from '@eneco/dc/useDC';
import { Bleed, Card, Box, Divider, Stack, Badge, Text, TextLink, VisuallyHidden, Heading } from '@eneco/sparky';
import { HeadingProps } from '@eneco/sparky/types';

import { Price } from '..';
import { Price as PriceType, DetailsIconType } from '../../types';
import { DetailsIcon } from '../DetailsIcon/DetailsIcon';

interface CompoundOverviewProps {
  Header: typeof Header;
  Content: typeof Content;
  Details: typeof Details;
  Product: typeof Product;
  ProductSummary: typeof ProductSummary;
  Footer: typeof Footer;
}

interface HeaderProps {
  withBackground?: boolean;
  headingLevel?: HeadingProps['as'];
  isBold?: boolean;
}

interface ProductProps {
  type: DetailsIconType;
  title: string;
  subtitle?: string;
  costsMonthly: PriceType;
  costsYearly?: PriceType;
  tag?: string;
  hideIcon?: boolean;
  hideDivider?: boolean;
  isBold?: boolean;
}

interface DetailsProps {
  type: DetailsIconType;
  title: string;
  editButton?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
}

interface ProductSummaryProps {
  label?: string;
  costs?: PriceType;
  costDetailsTrigger?: JSX.Element;
}

const Header: FC<PropsWithChildren<HeaderProps>> = ({ withBackground, children, headingLevel = 'h3', isBold }) => {
  const { label } = useDC();

  if (withBackground) {
    let backgroundColor: Pick<ComponentProps<typeof Box>, 'backgroundColor'>['backgroundColor'];
    let textColor: Pick<ComponentProps<typeof Text>, 'color'>['color'];

    switch (label) {
      case 'oxxio':
        backgroundColor = 'backgroundCMSVarTwo';
        textColor = 'textInverted';
        break;

      default:
        backgroundColor = 'backgroundBrand';
        textColor = 'textInverted';
    }

    return (
      <Bleed top="6">
        <Box
          paddingX="4"
          paddingY="4"
          borderTopLeftRadius="m"
          borderTopRightRadius="m"
          backgroundColor={backgroundColor}>
          <Text color={textColor} weight={isBold ? 'bold' : undefined}>
            {children}
          </Text>
        </Box>
      </Bleed>
    );
  } else {
    return (
      <>
        <Box paddingX="6" paddingBottom="1">
          <Heading as={headingLevel} size="3XS">
            {isBold ? <Text weight="bold">{children}</Text> : children}
          </Heading>
        </Box>
        <Divider />
      </>
    );
  }
};

interface ContentProps {
  isBold?: boolean;
}

const Content: FC<PropsWithChildren<ContentProps>> = ({ children, isBold }) => (
  <Box paddingX="6">{isBold ? <Text weight="bold">{children}</Text> : children}</Box>
);

const Product: FC<PropsWithChildren<ProductProps>> = ({
  type,
  title,
  subtitle,
  costsMonthly,
  costsYearly,
  tag,
  hideIcon,
  children,
  hideDivider,
  isBold,
}) => (
  <Box paddingX="6">
    <Stack direction="row" alignY="start" gap="2">
      {!hideIcon && (
        <Stack.Item shrink={false}>
          <DetailsIcon type={type} />
        </Stack.Item>
      )}

      <Stack.Item grow={true}>
        <Heading as="h2" size="3XS">
          {isBold ? <Text weight="bold">{title}</Text> : title}
        </Heading>

        {subtitle && (
          <Text size="BodyS" color="textLowEmphasis">
            {subtitle}
          </Text>
        )}

        {tag && (
          <Box paddingTop="1">
            <Badge>{tag}</Badge>
          </Box>
        )}

        {children && <Box paddingTop="1">{children}</Box>}
      </Stack.Item>

      <Box paddingLeft="2">
        <Stack direction="column" gap={2}>
          {costsMonthly && (
            <Stack.Item shrink={false}>
              <Price suffix={costsMonthly.period} previousValue={costsMonthly.previousValue}>
                {costsMonthly.value}
              </Price>
            </Stack.Item>
          )}

          {costsYearly && (
            <Stack.Item shrink={false}>
              <Text size={'BodyS'}>
                <Price suffix={costsYearly.period} previousValue={costsYearly.previousValue} emphasis="low">
                  {costsYearly.value}
                </Price>
              </Text>
            </Stack.Item>
          )}
        </Stack>
      </Box>
    </Stack>

    {!hideDivider && (
      <Box paddingTop="3">
        <Divider />
      </Box>
    )}
  </Box>
);

const Details: FC<PropsWithChildren<DetailsProps>> = ({ type, title, editButton, children }) => (
  <StyledBox paddingX="6" paddingTop="3">
    <Stack direction="row" alignY="start" gap="2" wrap>
      <Stack.Item shrink={false}>
        <DetailsIcon type={type} />
      </Stack.Item>

      <Stack.Item grow={true}>
        <Box paddingTop="1">
          <Text size="BodyXS" weight="bold">
            {title}
          </Text>
          <Box paddingTop="1">
            <Text size="BodyS" color="textLowEmphasis">
              <Stack direction="column">{children}</Stack>
            </Text>
          </Box>
        </Box>
      </Stack.Item>

      {editButton && (
        <Stack.Item shrink={false}>
          <Box paddingLeft="2">
            <Text size="BodyS" color="textLowEmphasis">
              <TextLink href={editButton.href} onClick={editButton.onClick}>
                {editButton.label} <VisuallyHidden>{title}</VisuallyHidden>
              </TextLink>
            </Text>
          </Box>
        </Stack.Item>
      )}
    </Stack>
  </StyledBox>
);

const ProductSummary: FC<PropsWithChildren<ProductSummaryProps>> = ({ label, costs, costDetailsTrigger, children }) => (
  <Box paddingX="6">
    <Stack direction="row" alignY="start">
      <Stack.Item grow={true}>
        <Stack direction="column" alignX="start">
          {label && (
            <Heading as="h3" size="3XS">
              {label}
            </Heading>
          )}

          {costDetailsTrigger && <Stack.Item>{costDetailsTrigger}</Stack.Item>}
        </Stack>
      </Stack.Item>

      {costs && (
        <Stack.Item shrink={false}>
          <Box paddingLeft="2">
            <Price suffix={costs.period} previousValue={costs.previousValue}>
              {costs.value}
            </Price>
          </Box>
        </Stack.Item>
      )}
    </Stack>

    {children}
  </Box>
);

const Footer: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <Box paddingX="6">
    <Box paddingBottom="5">
      <Divider />
    </Box>

    {children}
  </Box>
);

export const Overview: FC<PropsWithChildren<unknown>> & CompoundOverviewProps = ({ children }) => (
  <Card overflow="hidden">
    <Box paddingY="6">
      <Stack direction="column" gap="4">
        {children}
      </Stack>
    </Box>
  </Card>
);

Overview.Header = Header;
Overview.Content = Content;
Overview.Product = Product;
Overview.Details = Details;
Overview.ProductSummary = ProductSummary;
Overview.Footer = Footer;
